import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CaseStudyCallout = ({ caseStudy, variant = 'default' }) => {
  const variantStyles = {
    default: 'bg-white border-gray-200',
    featured: 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200',
    compact: 'bg-gray-50 border-gray-100'
  };

  const iconColors = {
    'cost-reduction': 'text-green-600 bg-green-100',
    'time-savings': 'text-blue-600 bg-blue-100',
    'efficiency': 'text-purple-600 bg-purple-100',
    'revenue': 'text-orange-600 bg-orange-100'
  };

  return (
    <div className={`rounded-xl shadow-executive border p-6 ${variantStyles?.[variant]} transition-all duration-300 hover:shadow-executive-lg`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
            <img 
              src={caseStudy?.companyLogo} 
              alt={`${caseStudy?.companyName} logo`}
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h4 className="font-bold text-text-primary">{caseStudy?.companyName}</h4>
            <p className="text-sm text-text-secondary">{caseStudy?.industry} • {caseStudy?.size}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-text-secondary">{caseStudy?.implementationTime}</div>
          <div className="text-xs font-medium text-brand-primary">{caseStudy?.phase} Implementation</div>
        </div>
      </div>
      {/* Challenge */}
      <div className="mb-4">
        <h5 className="font-semibold text-text-primary mb-2 flex items-center">
          <Icon name="AlertCircle" size={16} className="text-orange-500 mr-2" />
          Challenge
        </h5>
        <p className="text-sm text-text-secondary leading-relaxed">{caseStudy?.challenge}</p>
      </div>
      {/* Solution */}
      <div className="mb-6">
        <h5 className="font-semibold text-text-primary mb-2 flex items-center">
          <Icon name="Lightbulb" size={16} className="text-blue-500 mr-2" />
          AI Core Solution
        </h5>
        <p className="text-sm text-text-secondary leading-relaxed">{caseStudy?.solution}</p>
      </div>
      {/* Key Metrics */}
      <div className="mb-6">
        <h5 className="font-semibold text-text-primary mb-3">Key Results</h5>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {caseStudy?.metrics?.map((metric, index) => (
            <div key={index} className="text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${iconColors?.[metric?.type] || 'text-gray-600 bg-gray-100'}`}>
                <Icon name={metric?.icon} size={20} />
              </div>
              <div className="text-lg font-bold text-text-primary">{metric?.value}</div>
              <div className="text-xs text-text-secondary">{metric?.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Technologies Used */}
      <div className="mb-6">
        <h5 className="font-semibold text-text-primary mb-3">Technologies Implemented</h5>
        <div className="flex flex-wrap gap-2">
          {caseStudy?.technologies?.map((tech, index) => (
            <span 
              key={index}
              className="px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-medium text-text-primary"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
      {/* Quote */}
      {caseStudy?.testimonial && (
        <div className="mb-6 p-4 bg-white/50 rounded-lg border border-gray-100">
          <blockquote className="text-sm text-text-secondary italic leading-relaxed mb-3">
            "{caseStudy?.testimonial?.quote}"
          </blockquote>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <Icon name="User" size={16} className="text-gray-500" />
            </div>
            <div>
              <div className="text-sm font-medium text-text-primary">{caseStudy?.testimonial?.author}</div>
              <div className="text-xs text-text-secondary">{caseStudy?.testimonial?.title}</div>
            </div>
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          size="sm"
          iconName="FileText"
          iconPosition="left"
          className="flex-1"
        >
          Read Full Case Study
        </Button>
        <Button
          variant="default"
          size="sm"
          iconName="MessageCircle"
          iconPosition="left"
          className="flex-1 bg-brand-primary hover:bg-brand-primary/90"
        >
          Discuss Similar Project
        </Button>
      </div>
      {/* Implementation Timeline */}
      {variant === 'featured' && caseStudy?.timeline && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h5 className="font-semibold text-text-primary mb-3">Implementation Timeline</h5>
          <div className="space-y-2">
            {caseStudy?.timeline?.map((phase, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-brand-primary rounded-full flex-shrink-0"></div>
                <div className="flex-1 flex justify-between items-center">
                  <span className="text-sm text-text-primary">{phase?.phase}</span>
                  <span className="text-xs text-text-secondary">{phase?.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStudyCallout;